import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Button } from '@/ui/button/button';
import { FlexContainer } from '@/ui/flexbox/flexbox';

const MAX_VISIBLE_PAGES = 5;

interface PaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

const getPageNumbers = (
  currentPage: number,
  totalPages: number,
): (number | 'ellipsis')[] => {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const halfVisible = Math.floor(MAX_VISIBLE_PAGES / 2);

  if (currentPage <= halfVisible) {
    for (let i = 1; i <= MAX_VISIBLE_PAGES - 1; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  } else if (currentPage >= totalPages - halfVisible) {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = totalPages - (MAX_VISIBLE_PAGES - 2); i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return pages;
};

export const Pagination = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
}: PaginationProps): React.ReactElement | null => {
  const { t } = useTranslation('default');
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers(page, totalPages);
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;

  return (
    <PaginationContainer fullWidth justify="center" gap="8px">
      <NavigationButton
        variant="secondary"
        onClick={() => onPageChange(page - 1)}
        disabled={isFirstPage}
      >
        {t`previous`}
      </NavigationButton>
      <PageNumbersContainer gap="8px">
        {pageNumbers.map((pageNum, index) =>
          pageNum === 'ellipsis' ? (
            <Ellipsis key={`ellipsis-${index}`}>…</Ellipsis>
          ) : (
            <PageNumberButton
              key={pageNum}
              variant={pageNum === page ? 'primary' : 'secondary'}
              onClick={() => onPageChange(pageNum)}
              isActive={pageNum === page}
            >
              {pageNum}
            </PageNumberButton>
          ),
        )}
      </PageNumbersContainer>
      <NavigationButton
        variant="secondary"
        onClick={() => onPageChange(page + 1)}
        disabled={isLastPage}
      >
        {t`next`}
      </NavigationButton>
    </PaginationContainer>
  );
};

const PaginationContainer = styled(FlexContainer)`
  flex-wrap: wrap;
`;

const PageNumbersContainer = styled(FlexContainer)`
  flex-wrap: wrap;
`;

const Ellipsis = styled('span')`
  display: flex;
  align-items: center;
  padding: 0 4px;
  ${({ theme }) => theme.typography.acrossPlatform.small}
  color: ${({ theme }) => theme.colors.gray.c10};
`;

const PageNumberButton = styled(Button)<{ isActive?: boolean }>`
  && {
    align-items: center;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    height: 40px;
    justify-content: center;
    line-height: 20px;
    min-width: 40px;
    padding: 0;
    transition:
      background-color 150ms ease,
      color 150ms ease;
    width: 40px;

    background-color: ${({ isActive }) =>
      isActive ? '#5B5FED' : 'transparent'};
    color: ${({ isActive }) => (isActive ? '#FFFFFF' : '#666D80')};

    &:hover {
      background-color: ${({ isActive }) => (isActive ? '#5B5FED' : '#F3F4F6')};
      color: ${({ isActive }) => (isActive ? '#FFFFFF' : '#666D80')};
    }
  }
`;

const NavigationButton = styled(Button)`
  && {
    align-items: center;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: 8px;
    color: #666d80;
    font-size: 14px;
    font-weight: 500;
    justify-content: center;
    line-height: 20px;
    padding: 8px 16px;
    transition:
      background-color 150ms ease,
      color 150ms ease;

    &:hover {
      background-color: #f3f4f6;
      color: #666d80;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;
